<script lang="ts">
    import { onMount } from 'svelte';
    import { scale, slide } from 'svelte/transition';
    import { patternsInfo } from '../fixtures';
    import CandlestickChart, { type LogicalRange } from '../Charts/PatternChart.svelte';
    import { fetchCandlesWithPatterns, type ApiPattern, type IndicatorPoint } from '../api/klines';
    import type { CandlestickData } from 'lightweight-charts';
    import { getOpenPositions } from './Home';
    import { CandlestickPaterns } from '../Charts/Panel';
    import ChartSettings from '../Charts/Panel/ChartSettings.svelte';
    import Toolbar from '../Charts/Toolbar.svelte';
    import Modal from '../Modal.svelte';
    import ChartPaterns from '../Charts/Panel/Patterns/ChartPaterns.svelte';
    import { tooltip } from '../actions/tooltip';
    import AnalysisPanel from '../Charts/AnalysisPanel.svelte';
    import { fetchAnalysis, fetchFuturesBalance, type AnalysisResult } from '../api/analysis';
    import { fetchSlTpOrders, fetchSymbols, type SlTpOrder } from '../api/futures';

    interface CoinData {
        candles: CandlestickData[];
        patterns: ApiPattern[];
        indicators: IndicatorPoint[];
    }

    let positions:    tradePosition[]             = $state([]);
    let slTpOrders:   SlTpOrder[]                 = $state([]);
    let limit:        number                       = $state(1000);
    let columnPerPage = $state(localStorage.getItem('columnPerPage') ?? '6');
    let coinList:     string[]                     = $state(JSON.parse(localStorage.getItem('coinList') || '["BTCUSDT","ETHUSDT"]'));
    let coinIntervals: Record<string, string>      = $state(JSON.parse(localStorage.getItem('coinIntervals') || '{}'));
    let coinsData:    Record<string, CoinData>     = $state({});
    let loadingMore:   Record<string, boolean>       = $state({});

    // Управління монетами
    let coinsOpen        = $state(localStorage.getItem('coins_section_open') === '1');
    let availableSymbols = $state<string[]>([]);
    let coinSearch       = $state('');
    let showDropdown     = $state(false);

    $effect(() => { localStorage.setItem('coins_section_open', coinsOpen ? '1' : '0'); });

    function saveCoinList() {
        localStorage.setItem('coinList', JSON.stringify(coinList));
    }

    function addCoin(symbol: string) {
        const s = symbol.toUpperCase();
        if (!coinList.includes(s)) {
            coinList = [...coinList, s];
            if (!coinIntervals[s]) coinIntervals[s] = '30m';
            saveCoinList();
            fetchData();
        }
        coinSearch   = '';
        showDropdown = false;
    }

    function removeCoin(symbol: string) {
        coinList = coinList.filter(c => c !== symbol);
        delete coinsData[symbol];
        saveCoinList();
    }

    let filteredSymbols = $derived(
        coinSearch.length >= 1
            ? availableSymbols
                .filter(s => s.includes(coinSearch.toUpperCase()) && !coinList.includes(s))
                .slice(0, 30)
            : []
    );

    let analysisData:  Record<string, AnalysisResult> = $state({});
    let analysisLoading: Record<string, boolean>    = $state({});
    let balanceUsdt = $state(100);
    let apiKeysError = $state<string | null>(null);

    // Завантажуємо баланс один раз при старті (якщо авторизований)
    fetchFuturesBalance().then(b => { balanceUsdt = b; }).catch((e: any) => {
        if (e?.status === 400) apiKeysError = e.message;
    });
    let patterns:     Record<string, boolean>      = $state(Object.fromEntries(patternsInfo.map(p => [p.slogan, false])));
    let showModal        = $state(false);
    let modalCoin        = $state<string | null>(null);
    let modalInterval    = $state('30m');
    let modalData        = $state<CoinData | null>(null);
    let modalLoadingMore  = $state(false);
    let modalInitialRange = $state<LogicalRange | null>(null);
    let modalCoinSearch      = $state('');
    let modalCoinEditing     = $state(false);

    let filteredModalSymbols = $derived(
        modalCoinSearch.length >= 1
            ? availableSymbols.filter(s => s.includes(modalCoinSearch.toUpperCase())).slice(0, 40)
            : []
    );

    async function switchModalCoin(newCoin: string) {
        modalCoin        = newCoin;
        modalCoinEditing = false;
        modalCoinSearch  = '';
        modalInterval    = loadModalInterval(newCoin);
        modalInitialRange = loadModalRange(newCoin);
        modalData        = null;
        await loadModalData();
    }

    let modalMenuOpen        = $state(localStorage.getItem('modal_menu_open') === '1');
    let modalCandlestickOpen = $state(localStorage.getItem('modal_candlestick_open') === '1');
    let modalChartOpen       = $state(localStorage.getItem('modal_chart_open') === '1');
    let modalEmaOpen         = $state(localStorage.getItem('modal_ema_open') === '1');

    $effect(() => { localStorage.setItem('modal_menu_open',        modalMenuOpen        ? '1' : '0'); });
    $effect(() => { localStorage.setItem('modal_candlestick_open', modalCandlestickOpen ? '1' : '0'); });
    $effect(() => { localStorage.setItem('modal_chart_open',       modalChartOpen       ? '1' : '0'); });
    $effect(() => { localStorage.setItem('modal_ema_open',         modalEmaOpen         ? '1' : '0'); });

    // Helpers для localStorage стану модалки
    function saveModalInterval(coin: string, interval: string) {
        localStorage.setItem(`modal_interval_${coin}`, interval);
    }
    function loadModalInterval(coin: string): string {
        return localStorage.getItem(`modal_interval_${coin}`) ?? coinIntervals[coin] ?? '30m';
    }
    function saveModalRange(coin: string, range: LogicalRange) {
        localStorage.setItem(`modal_range_${coin}`, JSON.stringify(range));
    }
    function loadModalRange(coin: string): LogicalRange | null {
        const raw = localStorage.getItem(`modal_range_${coin}`);
        return raw ? JSON.parse(raw) : null;
    }
    let candlestickPatternsShow = $state(false);
    let chartPatternsShow       = $state(false);
    let indicatorsShow          = $state(false);
    let showEma9        = $state(false);
    let showEma21       = $state(false);
    let showEma200      = $state(false);
    let showSupertrend  = $state(false);

    for (const coin of coinList) {
        if (!coinIntervals[coin]) coinIntervals[coin] = '30m';
    }

    async function fetchData() {
        if (apiKeysError) return;
        for (const coin of coinList) {
            try {
                const result = await fetchCandlesWithPatterns(coin, coinIntervals[coin] ?? '30m', limit);
                coinsData[coin] = { candles: result.candles, patterns: result.patterns, indicators: result.indicators };
            } catch (e) {
                console.warn(`Failed to fetch data for ${coin}:`, e);
            }
        }
        [positions, slTpOrders] = await Promise.all([
            getOpenPositions(),
            fetchSlTpOrders().catch((e: any) => {
                if (e?.status === 400) apiKeysError = e.message;
                return [];
            }) as Promise<SlTpOrder[]>,
        ]);
    }

    async function loadMoreHistory(coin: string) {
        if (loadingMore[coin]) return;
        const existing = coinsData[coin];
        if (!existing || existing.candles.length === 0) return;

        // Час найстаршої свічки − 1 мс → завантажити те, що було до неї
        const oldest = existing.candles[0] as any;
        const oldestMs = (oldest.time as number) * 1000;
        const endTime = oldestMs - 1;

        loadingMore[coin] = true;
        try {
            const result = await fetchCandlesWithPatterns(coin, coinIntervals[coin] ?? '30m', 500, endTime);
            if (result.candles.length === 0) return;

            // Злити: старіші + наявні (уникнути дублів за time)
            const existingTimes = new Set(existing.candles.map((c: any) => c.time));
            const newCandles = result.candles.filter((c: any) => !existingTimes.has(c.time));
            if (newCandles.length === 0) return;

            const mergedCandles = [...newCandles, ...existing.candles];

            // Індикатори: злити аналогічно
            const existingIndTimes = new Set(existing.indicators.map(i => i.time));
            const newIndicators = result.indicators.filter(i => !existingIndTimes.has(i.time));
            const mergedIndicators = [...newIndicators, ...existing.indicators];

            coinsData[coin] = {
                candles: mergedCandles,
                patterns: [...result.patterns, ...existing.patterns],
                indicators: mergedIndicators,
            };
        } finally {
            loadingMore[coin] = false;
        }
    }

    const ANALYSIS_TTL_MS = 10 * 60 * 1000; // 10 хвилин

    function analysisStorageKey(coin: string, interval: string) {
        return `analysis_cache_${coin}_${interval}`;
    }

    function saveAnalysisCache(coin: string, interval: string, result: AnalysisResult) {
        try {
            localStorage.setItem(analysisStorageKey(coin, interval), JSON.stringify({
                ts: Date.now(),
                result,
            }));
        } catch {}
    }

    function loadAnalysisCache(coin: string, interval: string): AnalysisResult | null {
        try {
            const raw = localStorage.getItem(analysisStorageKey(coin, interval));
            if (!raw) return null;
            const { ts, result } = JSON.parse(raw);
            if (Date.now() - ts > ANALYSIS_TTL_MS) return null;
            return result as AnalysisResult;
        } catch {
            return null;
        }
    }

    async function analyzeChart(coin: string) {
        if (analysisLoading[coin]) return;
        analysisLoading[coin] = true;
        try {
            const result = await fetchAnalysis(coin, coinIntervals[coin] ?? '30m', balanceUsdt);
            analysisData[coin] = result;
            if (result.success) saveAnalysisCache(coin, coinIntervals[coin] ?? '30m', result);
        } finally {
            analysisLoading[coin] = false;
        }
    }

    async function loadModalData() {
        if (!modalCoin) return;
        const result = await fetchCandlesWithPatterns(modalCoin, modalInterval, 1000);
        modalData = { candles: result.candles, patterns: result.patterns, indicators: result.indicators };
    }

    async function setModalInterval(interval: string) {
        if (modalCoin) saveModalInterval(modalCoin, interval);
        modalInterval     = interval;
        modalInitialRange = null;   // новий таймфрейм → fitContent, не відновлювати range
        modalData         = null;
        await loadModalData();
    }

    async function loadMoreHistoryModal() {
        if (modalLoadingMore || !modalCoin || !modalData || modalData.candles.length === 0) return;
        const oldest = modalData.candles[0] as any;
        const endTime = (oldest.time as number) * 1000 - 1;

        modalLoadingMore = true;
        try {
            const result = await fetchCandlesWithPatterns(modalCoin, modalInterval, 500, endTime);
            if (result.candles.length === 0) return;

            const existingTimes = new Set(modalData.candles.map((c: any) => c.time));
            const newCandles = result.candles.filter((c: any) => !existingTimes.has(c.time));
            if (newCandles.length === 0) return;

            const existingIndTimes = new Set(modalData.indicators.map(i => i.time));
            const newIndicators = result.indicators.filter(i => !existingIndTimes.has(i.time));

            modalData = {
                candles: [...newCandles, ...modalData.candles],
                patterns: [...result.patterns, ...modalData.patterns],
                indicators: [...newIndicators, ...modalData.indicators],
            };
        } finally {
            modalLoadingMore = false;
        }
    }

    async function openChartInModal(coin: string) {
        modalCoin         = coin;
        modalCoinEditing  = false;
        modalCoinSearch   = '';
        modalInterval     = loadModalInterval(coin);
        modalInitialRange = loadModalRange(coin);   // відновити збережену позицію
        modalData         = null;
        showModal         = true;
        await loadModalData();
    }

    onMount(() => {
        fetchSymbols().then(r => { availableSymbols = r.symbols ?? []; }).catch((e: any) => {
            if (e?.status === 400) apiKeysError = e.message;
        });

        // Відновити свіжі аналізи з кешу (не старші 10 хв)
        for (const coin of coinList) {
            const interval = coinIntervals[coin] ?? '30m';
            const cached = loadAnalysisCache(coin, interval);
            if (cached) analysisData[coin] = cached;
        }

        (async () => {
            positions  = await getOpenPositions();
            slTpOrders = await fetchSlTpOrders().catch((e: any) => {
                if (e?.status === 400) apiKeysError = e.message;
                return [];
            }) as SlTpOrder[];
            await fetchData();
        })();

        const interval = setInterval(fetchData, 5_000);
        return () => clearInterval(interval);
    });
</script>

<div class="chart">
    {#if apiKeysError}
        <div class="api-keys-warning">
            ⚠️ {apiKeysError} — <a href="/user">Налаштування</a>
        </div>
    {/if}
    <nav class="chart__left">
        <!-- Верхня частина: меню юзера + вибір колонок -->
        <div class="sidebar-top">
            <ChartSettings bind:columnPerPage />
        </div>

        <!-- Секція: Монети -->
        <div class="sidebar-section">
            <button
                class="sidebar-item"
                class:sidebar-item--active={coinsOpen}
                onclick={() => coinsOpen = !coinsOpen}
            >
                <span class="sidebar-item-icon">◎</span>
                <span class="sidebar-item-label">Монети</span>
                <span class="sidebar-item-arrow">{coinsOpen ? '▾' : '▸'}</span>
            </button>
            {#if coinsOpen}
                <div class="sidebar-submenu coins-submenu" transition:slide={{ duration: 180 }}>
                    <!-- Список активних монет -->
                    <ul class="coin-list">
                        {#each coinList as coin}
                            <li class="coin-item">
                                <span class="coin-name">{coin}</span>
                                <button class="coin-remove" onclick={() => removeCoin(coin)} title="Видалити">×</button>
                            </li>
                        {/each}
                    </ul>

                    <!-- Додати монету -->
                    <div class="coin-add-wrap">
                        <div class="coin-add-input-row">
                            <input
                                class="coin-add-input"
                                type="text"
                                placeholder="Знайти символ..."
                                bind:value={coinSearch}
                                onfocus={() => showDropdown = true}
                                onblur={() => setTimeout(() => showDropdown = false, 150)}
                                onkeydown={(e) => {
                                    if (e.key === 'Enter' && filteredSymbols.length > 0) addCoin(filteredSymbols[0]);
                                    if (e.key === 'Escape') { coinSearch = ''; showDropdown = false; }
                                }}
                            />
                        </div>
                        {#if showDropdown && filteredSymbols.length > 0}
                            <ul class="coin-dropdown" transition:slide={{ duration: 120 }}>
                                {#each filteredSymbols as s}
                                    <li>
                                        <button class="coin-dropdown-item" onmousedown={() => addCoin(s)}>{s}</button>
                                    </li>
                                {/each}
                            </ul>
                        {/if}
                    </div>
                </div>
            {/if}
        </div>

        <!-- Секція: Патерни -->
        <div class="sidebar-section">
            <div class="sidebar-section-header">Патерни</div>

            <button
                class="sidebar-item"
                class:sidebar-item--active={candlestickPatternsShow}
                onclick={() => candlestickPatternsShow = !candlestickPatternsShow}
            >
                <span class="sidebar-item-icon">🕯</span>
                <span class="sidebar-item-label">Свічкові</span>
                <span class="sidebar-item-arrow">{candlestickPatternsShow ? '▾' : '▸'}</span>
            </button>
            {#if candlestickPatternsShow}
                <div class="sidebar-submenu" transition:slide={{ duration: 180 }}>
                    <CandlestickPaterns bind:patterns />
                </div>
            {/if}

            <button
                class="sidebar-item"
                class:sidebar-item--active={chartPatternsShow}
                onclick={() => chartPatternsShow = !chartPatternsShow}
            >
                <span class="sidebar-item-icon">📐</span>
                <span class="sidebar-item-label">Графічні</span>
                <span class="sidebar-item-arrow">{chartPatternsShow ? '▾' : '▸'}</span>
            </button>
            {#if chartPatternsShow}
                <div class="sidebar-submenu" transition:slide={{ duration: 180 }}>
                    <ChartPaterns bind:patterns />
                </div>
            {/if}
        </div>

        <!-- Секція: Індикатори -->
        <div class="sidebar-section">
            <div class="sidebar-section-header">Індикатори</div>

            <button
                class="sidebar-item"
                class:sidebar-item--active={indicatorsShow}
                onclick={() => indicatorsShow = !indicatorsShow}
            >
                <span class="sidebar-item-icon">〰</span>
                <span class="sidebar-item-label">EMA</span>
                <span class="sidebar-item-arrow">{indicatorsShow ? '▾' : '▸'}</span>
            </button>
            {#if indicatorsShow}
                <div class="sidebar-submenu" transition:slide={{ duration: 180 }}>
                    <label class="ema-toggle">
                        <input type="checkbox" bind:checked={showEma9}
                            use:tooltip={() => 'EMA 9 — дуже короткострокова.\nРеагує першою на зміну ціни.\n\nВикористання:\n• Визначає поточний короткостроковий імпульс\n• Перетин EMA9 знизу вгору EMA21 → сигнал лонгу\n• Перетин EMA9 зверху вниз EMA21 → сигнал шорту'}
                        />
                        <span class="ema-dot" style="background:#2196f3"></span>
                        EMA 9
                    </label>
                    <label class="ema-toggle">
                        <input type="checkbox" bind:checked={showEma21}
                            use:tooltip={() => 'EMA 21 — короткострокова.\nФільтрує шум краще ніж EMA 9.\n\nВикористання:\n• Рівень підтримки/опору в тренді\n• Ціна вище EMA21 → бичачий імпульс\n• Ціна нижче EMA21 → ведмежий імпульс\n• Комбінуй з EMA9 для crossover сигналів'}
                        />
                        <span class="ema-dot" style="background:#ff9800"></span>
                        EMA 21
                    </label>
                    <label class="ema-toggle">
                        <input type="checkbox" bind:checked={showEma200}
                            use:tooltip={() => 'EMA 200 — довгострокова, "лінія ринку".\nВикористовується більшістю трейдерів.\n\nВикористання:\n• Ціна вище EMA200 → бичачий ринок, надавай перевагу лонгам\n• Ціна нижче EMA200 → ведмежий ринок, надавай перевагу шортам\n• EMA200 — сильний рівень підтримки/опору\n• Відбій від EMA200 = потужний сигнал входу'}
                        />
                        <span class="ema-dot" style="background:#9c27b0"></span>
                        EMA 200
                    </label>
                </div>
            {/if}

            <label class="ema-toggle mt-1">
                <input type="checkbox" bind:checked={showSupertrend}
                    use:tooltip={() => 'SuperTrend (ATR 10, ×3) — трендовий індикатор.\n\nЗелена лінія → бичачий тренд (ціна вище лінії)\nЧервона лінія → ведмежий тренд (ціна нижче лінії)\n\nВикористання:\n• Лінія нижче ціни = тримай лонг\n• Лінія вище ціни = тримай шорт\n• Перетин = зміна тренду, сигнал виходу/входу\n• Лінія = природний рівень стоп-лосу'}
                />
                <span class="ema-dot" style="background:#26a69a"></span>
                SuperTrend
            </label>
        </div>
    </nav>

    <div class="chart__right row p-2">
        {#each Object.entries(coinsData) as [coin, data]}
            <div class="col col-sm-{columnPerPage}">
                <h4>{coin}</h4>
                <CandlestickChart
                    candles={data.candles}
                    apiPatterns={data.patterns}
                    {patterns}
                    {positions}
                    {slTpOrders}
                    {coin}
                    {columnPerPage}
                    indicators={data.indicators}
                    {showEma9}
                    {showEma21}
                    {showEma200}
                    {showSupertrend}
                    onLoadMore={() => loadMoreHistory(coin)}
                    isLoadingMore={loadingMore[coin] ?? false}
                />
                <Toolbar
                    {fetchData}
                    bind:coinIntervals
                    {coin}
                    {openChartInModal}
                    lastIndicator={data.indicators?.at(-1)}
                />
                <AnalysisPanel
                    result={analysisData[coin] ?? null}
                    loading={analysisLoading[coin] ?? false}
                    onAnalyze={() => analyzeChart(coin)}
                    currentPrice={(data.candles?.at(-1) as any)?.close ?? 0}
                    {balanceUsdt}
                    symbol={coin}
                />
            </div>
        {/each}
    </div>
</div>

{#if showModal && modalCoin}
    <Modal bind:showModal size="xxl">
        {#snippet body()}
            <div style="width:100%">
                <!-- Toolbar: гамбургер + монета + таймфрейми -->
                <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
                    <button
                        class="btn btn-sm p-1 lh-1"
                        class:btn-secondary={modalMenuOpen}
                        class:btn-outline-secondary={!modalMenuOpen}
                        onclick={() => modalMenuOpen = !modalMenuOpen}
                        title="Патерни та індикатори"
                    >
                        <svg width="18" height="14" viewBox="0 0 18 14" fill="currentColor">
                            <rect y="0"  width="18" height="2" rx="1"/>
                            <rect y="6"  width="18" height="2" rx="1"/>
                            <rect y="12" width="18" height="2" rx="1"/>
                        </svg>
                    </button>

                    <div class="modal-coin-select">
                        {#if modalCoinEditing}
                            <input
                                class="modal-coin-input"
                                type="text"
                                placeholder={modalCoin}
                                bind:value={modalCoinSearch}
                                autofocus
                                onblur={() => setTimeout(() => { modalCoinEditing = false; modalCoinSearch = ''; }, 150)}
                                onkeydown={(e) => {
                                    if (e.key === 'Enter' && filteredModalSymbols.length > 0) switchModalCoin(filteredModalSymbols[0]);
                                    if (e.key === 'Escape') { modalCoinEditing = false; modalCoinSearch = ''; }
                                }}
                            />
                            {#if filteredModalSymbols.length > 0}
                                <ul class="modal-coin-dropdown">
                                    {#each filteredModalSymbols as s}
                                        <li>
                                            <button
                                                class="modal-coin-dropdown-item"
                                                class:modal-coin-dropdown-item--active={s === modalCoin}
                                                onmousedown={() => switchModalCoin(s)}
                                            >{s}</button>
                                        </li>
                                    {/each}
                                </ul>
                            {/if}
                        {:else}
                            <button class="modal-coin-btn" onclick={() => { modalCoinEditing = true; modalCoinSearch = ''; }}>
                                {modalCoin} <span class="modal-coin-edit-icon">✎</span>
                            </button>
                        {/if}
                    </div>

                    {#each ['5m','15m','30m','1h','2h','4h','8h','1d','3d','1w'] as tf}
                        <button
                            class="btn btn-sm btn-outline-secondary p-1 lh-1"
                            class:active={modalInterval === tf}
                            onclick={() => setModalInterval(tf)}
                        >{tf}</button>
                    {/each}

                    {#if !modalData}
                        <span class="text-muted" style="font-size:0.8rem">Завантаження...</span>
                    {/if}
                </div>

                <!-- Контентна зона: сайдбар + графік -->
                <div class="modal-content-area">
                    <!-- Гамбургер-сайдбар -->
                    {#if modalMenuOpen}
                        <div class="modal-sidebar" transition:slide={{ axis: 'x', duration: 200 }}>
                            <h6 class="mb-2">Патерни</h6>

                            <button
                                class="btn btn-sm btn-outline-info w-100 mb-1"
                                class:active={modalCandlestickOpen}
                                onclick={() => modalCandlestickOpen = !modalCandlestickOpen}
                            >Свічкові</button>
                            {#if modalCandlestickOpen}
                                <div class="card card-body p-2 mb-2" transition:slide>
                                    <CandlestickPaterns bind:patterns />
                                </div>
                            {/if}

                            <button
                                class="btn btn-sm btn-outline-info w-100 mb-1"
                                class:active={modalChartOpen}
                                onclick={() => modalChartOpen = !modalChartOpen}
                            >Графічні</button>
                            {#if modalChartOpen}
                                <div class="card card-body p-2 mb-2" transition:slide>
                                    <ChartPaterns bind:patterns />
                                </div>
                            {/if}

                            <hr class="my-2" />
                            <h6 class="mb-2">Індикатори</h6>

                            <button
                                class="btn btn-sm btn-outline-secondary w-100 mb-1"
                                class:active={modalEmaOpen}
                                onclick={() => modalEmaOpen = !modalEmaOpen}
                            >EMA</button>
                            {#if modalEmaOpen}
                                <div class="card card-body p-2" transition:slide>
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" id="mema9"   bind:checked={showEma9} />
                                        <label class="form-check-label" for="mema9" style="color:#2196f3"
                                            use:tooltip={() => 'EMA 9 — дуже короткострокова.\nРеагує першою на зміну ціни.\n\nВикористання:\n• Визначає поточний короткостроковий імпульс\n• Перетин EMA9 знизу вгору EMA21 → сигнал лонгу\n• Перетин EMA9 зверху вниз EMA21 → сигнал шорту'}
                                        >EMA 9</label>
                                    </div>
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" id="mema21"  bind:checked={showEma21} />
                                        <label class="form-check-label" for="mema21" style="color:#ff9800"
                                            use:tooltip={() => 'EMA 21 — короткострокова.\nФільтрує шум краще ніж EMA 9.\n\nВикористання:\n• Рівень підтримки/опору в тренді\n• Ціна вище EMA21 → бичачий імпульс\n• Ціна нижче EMA21 → ведмежий імпульс\n• Комбінуй з EMA9 для crossover сигналів'}
                                        >EMA 21</label>
                                    </div>
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" id="mema200" bind:checked={showEma200} />
                                        <label class="form-check-label" for="mema200" style="color:#9c27b0"
                                            use:tooltip={() => 'EMA 200 — довгострокова, "лінія ринку".\nВикористовується більшістю трейдерів.\n\nВикористання:\n• Ціна вище EMA200 → бичачий ринок, надавай перевагу лонгам\n• Ціна нижче EMA200 → ведмежий ринок, надавай перевагу шортам\n• EMA200 — сильний рівень підтримки/опору\n• Відбій від EMA200 = потужний сигнал входу'}
                                        >EMA 200</label>
                                    </div>
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" id="mst" bind:checked={showSupertrend} />
                                        <label class="form-check-label" for="mst" style="color:#26a69a"
                                            use:tooltip={() => 'SuperTrend (ATR 10, ×3)\nЗелена → бичачий тренд\nЧервона → ведмежий тренд\nЛінія = рівень стоп-лосу'}
                                        >SuperTrend</label>
                                    </div>
                                </div>
                            {/if}
                        </div>
                    {/if}

                    <!-- Графік -->
                    {#if modalData}
                        <div class="modal-chart">
                            <CandlestickChart
                                candles={modalData.candles}
                                apiPatterns={modalData.patterns}
                                {patterns}
                                {positions}
                                {slTpOrders}
                                coin={modalCoin ?? undefined}
                                height={window.innerHeight - 120}
                                columnPerPage={1}
                                indicators={modalData.indicators}
                                {showEma9}
                                {showEma21}
                                {showEma200}
                                {showSupertrend}
                                onLoadMore={loadMoreHistoryModal}
                                isLoadingMore={modalLoadingMore}
                                initialRange={modalInitialRange}
                                onRangeChange={(r) => { if (modalCoin) saveModalRange(modalCoin, r); }}
                            />
                        </div>
                    {/if}
                </div>
            </div>
        {/snippet}
    </Modal>
{/if}

<style>
    /* ── Основний layout ─────────────────────────────── */
    .chart {
        display: flex;
        flex-direction: row;
        height: 100%;
    }

    .api-keys-warning {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 9999;
        background: #fff3cd;
        border-bottom: 1px solid #ffc107;
        color: #856404;
        padding: 6px 16px;
        font-size: 13px;
        text-align: center;
    }

    .api-keys-warning a {
        color: #856404;
        font-weight: 600;
    }

    /* ── Сайдбар ─────────────────────────────────────── */
    .chart__left {
        width: 210px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        background: #f8f9fa;
        border-right: 1px solid #e0e0e0;
        overflow-x: hidden;
        overflow-y: auto;
        height: 100vh;
        position: sticky;
        top: 0;
        z-index: 3;
    }

    .sidebar-top {
        padding: 10px 10px 6px;
        border-bottom: 1px solid #e0e0e0;
    }

    .sidebar-section {
        padding: 6px 0;
        border-bottom: 1px solid #e9ecef;
    }

    .sidebar-section-header {
        padding: 4px 12px 4px;
        font-size: 0.65rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #9e9e9e;
    }

    .sidebar-item {
        display: flex;
        align-items: center;
        gap: 7px;
        width: 100%;
        padding: 7px 12px;
        border: none;
        background: transparent;
        cursor: pointer;
        text-align: left;
        font-size: 0.82rem;
        color: #333;
        border-radius: 0;
        transition: background 0.12s;
    }
    .sidebar-item:hover {
        background: #edf2ff;
        color: #1a56db;
    }
    .sidebar-item--active {
        background: #e8f0fe;
        color: #1a56db;
        font-weight: 600;
    }
    .sidebar-item-icon {
        font-size: 0.9rem;
        flex-shrink: 0;
        width: 18px;
        text-align: center;
    }
    .sidebar-item-label {
        flex: 1;
    }
    .sidebar-item-arrow {
        font-size: 0.7rem;
        color: #aaa;
        flex-shrink: 0;
    }
    .sidebar-item--active .sidebar-item-arrow {
        color: #1a56db;
    }

    .sidebar-submenu {
        padding: 6px 10px 6px 16px;
        background: #fff;
        border-top: 1px solid #f0f0f0;
        border-bottom: 1px solid #f0f0f0;
    }

    /* ── Монети ──────────────────────────────────────── */
    .coins-submenu {
        padding: 6px 8px;
    }

    .coin-list {
        list-style: none;
        margin: 0 0 6px;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .coin-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 3px 6px;
        border-radius: 4px;
        background: #f1f3f5;
    }

    .coin-name {
        font-size: 0.78rem;
        font-weight: 600;
        color: #222;
    }

    .coin-remove {
        border: none;
        background: none;
        cursor: pointer;
        font-size: 1rem;
        line-height: 1;
        color: #bbb;
        padding: 0 2px;
        transition: color 0.1s;
    }
    .coin-remove:hover { color: #e53935; }

    .coin-add-wrap {
        position: relative;
    }

    .coin-add-input-row {
        display: flex;
        gap: 4px;
    }

    .coin-add-input {
        flex: 1;
        padding: 4px 7px;
        font-size: 0.75rem;
        border: 1px solid #d0d0d0;
        border-radius: 4px;
        outline: none;
        background: #fff;
        transition: border-color 0.15s;
    }
    .coin-add-input:focus { border-color: #1a56db; }

    .coin-dropdown {
        position: absolute;
        top: calc(100% + 2px);
        left: 0;
        right: 0;
        background: #fff;
        border: 1px solid #d0d0d0;
        border-radius: 5px;
        max-height: 180px;
        overflow-y: auto;
        z-index: 100;
        list-style: none;
        margin: 0;
        padding: 3px 0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.12);
    }

    .coin-dropdown-item {
        display: block;
        width: 100%;
        padding: 5px 10px;
        border: none;
        background: none;
        cursor: pointer;
        text-align: left;
        font-size: 0.78rem;
        color: #222;
        transition: background 0.1s;
    }
    .coin-dropdown-item:hover { background: #edf2ff; color: #1a56db; }

    /* EMA toggles */
    .ema-toggle {
        display: flex;
        align-items: center;
        gap: 7px;
        padding: 4px 2px;
        cursor: pointer;
        font-size: 0.78rem;
        color: #333;
        user-select: none;
    }
    .ema-toggle input[type="checkbox"] {
        width: 14px;
        height: 14px;
        cursor: pointer;
    }
    .ema-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    /* ── Права частина ───────────────────────────────── */
    .chart__right {
        flex: 1;
        min-width: 0;
        overflow-x: hidden;
        position: relative;
    }

    /* ── Селектор монети в модалці ──────────────────── */
    .modal-coin-select {
        position: relative;
    }

    .modal-coin-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: none;
        border: none;
        padding: 2px 6px;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 700;
        color: #212529;
        border-radius: 4px;
        transition: background 0.12s;
    }
    .modal-coin-btn:hover {
        background: #edf2ff;
        color: #1a56db;
    }
    .modal-coin-edit-icon {
        font-size: 0.7rem;
        opacity: 0.35;
    }
    .modal-coin-btn:hover .modal-coin-edit-icon {
        opacity: 0.8;
    }

    .modal-coin-input {
        padding: 3px 8px;
        font-size: 0.95rem;
        font-weight: 700;
        border: 2px solid #1a56db;
        border-radius: 5px;
        outline: none;
        width: 140px;
        background: #fff;
    }

    .modal-coin-dropdown {
        position: absolute;
        top: calc(100% + 2px);
        left: 0;
        min-width: 160px;
        background: #fff;
        border: 1px solid #d0d0d0;
        border-radius: 6px;
        max-height: 260px;
        overflow-y: auto;
        z-index: 9999;
        list-style: none;
        margin: 0;
        padding: 3px 0;
        box-shadow: 0 6px 20px rgba(0,0,0,0.15);
    }

    .modal-coin-dropdown-item {
        display: block;
        width: 100%;
        padding: 5px 12px;
        border: none;
        background: none;
        cursor: pointer;
        text-align: left;
        font-size: 0.82rem;
        font-weight: 600;
        color: #222;
        transition: background 0.1s;
    }
    .modal-coin-dropdown-item:hover { background: #edf2ff; color: #1a56db; }
    .modal-coin-dropdown-item--active { color: #1a56db; background: #f0f4ff; }

    /* ── Модалка ─────────────────────────────────────── */
    .modal-content-area {
        position: relative;
        display: flex;
        flex-direction: row;
        overflow: hidden;
    }

    .modal-sidebar {
        width: 220px;
        flex-shrink: 0;
        overflow-y: auto;
        padding: 8px;
        background: #fff;
        border-right: 1px solid #dee2e6;
        max-height: calc(100vh - 140px);
    }

    .modal-chart {
        flex: 1;
        min-width: 0;
    }

    /* ── Tooltip ─────────────────────────────────────── */
    :global(.__ind-tooltip) {
        position: fixed;
        background: #1e1e1e;
        color: #f0f0f0;
        padding: 9px 13px;
        border-radius: 7px;
        font-size: 0.76rem;
        width: 240px;
        z-index: 99999;
        pointer-events: none;
        line-height: 1.6;
        white-space: pre-line;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
    }
</style>
